/**
 * Block archive / criminal-record cancel while the person still has active pairs.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Users } from 'lucide-react';
import { InfoModal } from '@/components/common/modals/InfoModal';
import { getErrorMessage } from '@/utils/errors-utils';

/** Which blocked action ran while tutoring is in progress. */
export type ActivePairsBlockAction = 'archive' | 'cancelCriminalRecord';

/** Person whose pairs must be cleared before the action. */
export type ActivePairsPersonKind = 'tutor' | 'student';

/** Props for {@link ActivePairsBlockModal}. */
interface ActivePairsBlockModalProps {
  open: boolean;
  personUserId: string;
  personName: string;
  personKind: ActivePairsPersonKind;
  action: ActivePairsBlockAction;
  onClose: () => void;
}

/** Title for the blocked-action dialog. */
function getBlockTitle(action: ActivePairsBlockAction): string {
  if (action === 'archive') return 'Archivage impossible';
  return 'Annulation impossible';
}

/** Catalog copy explaining why the action cannot run yet. */
function getBlockMessage(
  personKind: ActivePairsPersonKind,
  action: ActivePairsBlockAction,
  personName: string,
): string {
  if (personKind === 'student') {
    return getErrorMessage({ code: 'studentActivePairsBlockArchive', studentName: personName });
  }
  if (action === 'archive') {
    return getErrorMessage({ code: 'tutorActivePairsBlockArchive', tutorName: personName });
  }
  return getErrorMessage({
    code: 'tutorActivePairsBlockCancelCriminalRecord',
    tutorName: personName,
  });
}

/** Router state that pins the pairs list to this person. */
function buildPairsFilterState(
  personKind: ActivePairsPersonKind,
  personUserId: string,
  personName: string,
): { tutorUserId?: string; studentUserId?: string; personName: string } {
  if (personKind === 'student') {
    return { studentUserId: personUserId, personName };
  }
  return { tutorUserId: personUserId, personName };
}

/** Explains that pairs must be dissociated first, then offers a jump to that list. */
export const ActivePairsBlockModal: React.FC<Readonly<ActivePairsBlockModalProps>> = ({
  open,
  personUserId,
  personName,
  personKind,
  action,
  onClose,
}) => {
  const navigate = useNavigate();

  function handleGoToPairs(): void {
    onClose();
    void navigate('/pairs', {
      state: buildPairsFilterState(personKind, personUserId, personName),
    });
  }

  if (!open) return null;

  return (
    <InfoModal
      open
      onClose={onClose}
      icon={AlertTriangle}
      title={getBlockTitle(action)}
      size="md"
      sections={[{ id: 'blocked', content: getBlockMessage(personKind, action, personName) }]}
      actions={[
        { id: 'cancel', label: 'Annuler', onClick: onClose, preset: 'modal-cancel' },
        {
          id: 'pairs',
          label: 'Voir les binômes',
          onClick: handleGoToPairs,
          preset: 'modal-confirm',
          icon: Users,
        },
      ]}
    />
  );
};
